import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Bell, Blocks, CalendarDays, ChevronRight, Home, LogOut, Menu, Palette, Server, Shield, UserCircle2 } from 'lucide-react'

import { ThemeToggle } from '@/components/theme-toggle'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLogoutMutation } from '@/hooks/useAuthQuery'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'

import { UserMenu } from './UserMenu'

interface NavbarProps {
  className?: string
}

const navItems = [
  { href: '/', label: '首页', icon: Home },
  { href: '/skins', label: '皮肤库', icon: Palette },
  { href: '/blueprints', label: '蓝图库', icon: Blocks },
  { href: '/servers', label: '服务器', icon: Server },
  { href: '/announcements', label: '公告', icon: Bell },
] as const

/**
 * 顶部导航栏组件
 * - 高度 64px (h-16)
 * - Logo 点击跳转主页
 * - 已登录：显示用户头像，点击跳转用户仪表板
 * - 未登录：显示「登录 / 注册」按钮
 * - 包含主题切换按钮
 */
export function Navbar({ className }: NavbarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const logoutMutation = useLogoutMutation()
  const { isAuthenticated, user, clearAuth, isAdmin: checkIsAdmin } = useAuthStore()
  const isAdmin = checkIsAdmin()
  const mobileShortcutItems = useMemo(() => {
    if (!isAuthenticated) {
      return []
    }

    const items = [
      { href: '/user', label: '玩家中心', icon: UserCircle2 },
      { href: '/user/checkin', label: '每日签到', icon: CalendarDays },
      { href: '/user/notifications', label: '通知中心', icon: Bell },
    ]

    if (isAdmin) {
      items.push({ href: '/admin', label: '管理后台', icon: Shield })
    }

    return items
  }, [isAdmin, isAuthenticated])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname, location.search])

  const handleLogoClick = () => {
    navigate('/')
  }

  const handleNavigate = (href: string) => {
    setMobileMenuOpen(false)
    navigate(href)
  }

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync()
    } catch {
      // 无论后端状态如何，前端都应清空本地会话
    } finally {
      clearAuth()
      setMobileMenuOpen(false)
      navigate('/')
    }
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-50 h-16 w-full border-b border-[#eadfce] bg-[#f8f1e5]/84 backdrop-blur-md transition-colors duration-300',
        'dark:border-neutral-800 dark:bg-[#1a1b1d]/84',
        className
      )}
    >
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          onClick={handleLogoClick}
          className="flex items-center gap-2 text-xl font-bold text-slate-900 transition-colors hover:text-indigo-600 dark:text-slate-100 dark:hover:text-indigo-400"
        >
          <img
            src="/site-logo.svg"
            alt="次梦领域"
            className="block h-8 w-auto shrink-0"
            style={{ imageRendering: 'pixelated' }}
          />
          <span className="hidden sm:inline">次梦领域</span>
        </button>

        <nav className="hidden items-center gap-6 md:flex">
          {navItems.map(item => (
            <NavLink key={item.href} href={item.href}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />

          {isAuthenticated ? (
            <UserMenu />
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <button
                onClick={() => navigate('/auth/login')}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-200',
                  'hover:bg-slate-100 hover:text-slate-900',
                  'dark:text-slate-300 dark:hover:bg-[#222427] dark:hover:text-slate-100'
                )}
              >
                登录
              </button>
              <button
                onClick={() => navigate('/auth/register')}
                className={cn(
                  'rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition-all duration-200',
                  'hover:bg-indigo-700 hover:shadow-md',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                  'dark:bg-indigo-600 dark:hover:bg-indigo-500'
                )}
              >
                注册
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg md:hidden',
              'text-slate-600 hover:bg-[#eef1f8] dark:text-slate-400 dark:hover:bg-[#222427]'
            )}
            aria-label="打开菜单"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <DialogContent className="app-dialog-surface top-20 z-[60] w-[calc(100vw-1.5rem)] max-w-[22rem] translate-y-0 gap-0 overflow-hidden p-0 shadow-[0_30px_70px_rgba(15,23,42,0.22)] md:hidden">
          <div className="border-b border-border/80 bg-card/55 px-4 pb-4 pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-border bg-secondary text-foreground">
                <Menu className="h-4 w-4" />
              </div>
              <div>
                <DialogTitle className="text-left text-base font-semibold text-foreground">
                  菜单
                </DialogTitle>
                <p className="mt-1 text-sm text-muted-foreground">次梦领域</p>
              </div>
            </div>
          </div>

          <div className="max-h-[calc(100vh-10rem)] space-y-4 overflow-y-auto px-4 py-4">
            <MenuSection title="浏览">
              {navItems.map(item => (
                <MenuLinkButton
                  key={item.href}
                  label={item.label}
                  icon={<item.icon className="h-4 w-4" />}
                  active={location.pathname === item.href}
                  onClick={() => handleNavigate(item.href)}
                />
              ))}
            </MenuSection>

            {isAuthenticated ? (
              <MenuSection title="我的入口">
                <div className="app-card-soft px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                        当前账号
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        {user?.username || '已登录用户'}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {isAdmin ? '可进入玩家区和管理区' : '可进入玩家区'}
                      </p>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-secondary text-foreground">
                      {isAdmin ? <Shield className="h-4 w-4" /> : <UserCircle2 className="h-4 w-4" />}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {mobileShortcutItems.map(item => {
                    const Icon = item.icon

                    return (
                      <MenuLinkButton
                        key={item.href}
                        onClick={() => handleNavigate(item.href)}
                        label={item.label}
                        icon={<Icon className="h-4 w-4" />}
                        active={location.pathname === item.href}
                      />
                    )
                  })}
                </div>

                <MenuLinkButton
                  label="退出登录"
                  icon={<LogOut className="h-4 w-4" />}
                  onClick={() => void handleLogout()}
                  destructive
                />
              </MenuSection>
            ) : (
              <section className="app-card-soft space-y-3 p-4">
                <div>
                  <p className="text-sm font-semibold text-foreground">开始使用面板</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    登录后可进入玩家中心、签到、查看通知和管理自己的账号。
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleNavigate('/auth/login')}
                    className="rounded-[1.05rem] border border-border bg-card/72 px-4 py-3 text-sm font-medium text-foreground transition hover:bg-accent"
                  >
                    登录
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNavigate('/auth/register')}
                    className="rounded-[1.05rem] bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-indigo-500"
                  >
                    注册
                  </button>
                </div>
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  )
}

interface NavLinkProps {
  href: string
  children: React.ReactNode
}

function MenuSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        {title}
      </p>
      <div className="space-y-2">{children}</div>
    </section>
  )
}

function MenuLinkButton({
  label,
  icon,
  active = false,
  destructive = false,
  onClick,
}: {
  label: string
  icon: React.ReactNode
  active?: boolean
  destructive?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between rounded-[1.2rem] border px-4 py-3 text-left text-sm font-medium transition',
        destructive
          ? 'border-red-500/20 bg-red-500/10 text-red-600 hover:bg-red-500/15 dark:text-red-300'
          : active
            ? 'border-emerald-500/25 bg-emerald-500/10 text-foreground'
            : 'border-border bg-card/72 text-foreground hover:bg-accent'
      )}
    >
      <span className="flex items-center gap-3">
        <span className={cn('text-muted-foreground', destructive && 'text-red-500 dark:text-red-300')}>
          {icon}
        </span>
        {label}
      </span>
      <ChevronRight className="h-4 w-4 opacity-50" />
    </button>
  )
}

function NavLink({ href, children }: NavLinkProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const isActive = location.pathname === href

  return (
    <button
      onClick={() => navigate(href)}
      className={cn(
        'text-sm font-medium transition-colors duration-200',
        isActive
          ? 'text-indigo-600 dark:text-indigo-400'
          : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
      )}
    >
      {children}
    </button>
  )
}
